import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const RefundPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <div className="container py-5">
        {/* Back Button */}
        <Link
          to="/"
          className="btn btn-link text-primary text-decoration-none mb-4 p-0"
        >
          <ArrowLeft className="me-2" size={16} />
          Back to Home
        </Link>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card border-0 shadow-lg">
              <div className="card-header bg-warning text-dark p-4">
                <div className="d-flex align-items-center gap-3">
                  <CreditCard size={32} />
                  <div>
                    <h1 className="h2 mb-1">Refund & Cancellation Policy</h1>
                    <p className="mb-0 opacity-90">Last updated: JULY 2025</p>
                  </div>
                </div>
              </div>

              <div className="card-body p-5">
                <div className="alert alert-warning d-flex align-items-start gap-3">
                  <AlertTriangle size={20} className="mt-1" />
                  <div>
                    <strong>Important:</strong> Please read our refund policy carefully before making any purchase. This policy outlines the conditions under which refunds may be granted.
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3 d-flex align-items-center gap-2">
                    <CheckCircle size={20} />
                    1. Refund Eligibility
                  </h2>
                  <div className="text-muted">
                    <p className="mb-3">You may be eligible for a refund under the following conditions:</p>
                    <ul>
                      <li><strong>Technical Issues:</strong> If our service fails to deliver your personalized college list due to technical problems on our end</li>
                      <li><strong>Duplicate Payment:</strong> If you accidentally make multiple payments for the same service</li>
                      <li><strong>Service Not Delivered:</strong> If we fail to provide the promised service within 48 hours of payment</li>
                      <li><strong>Billing Errors:</strong> If there's an error in the amount charged</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3 d-flex align-items-center gap-2">
                    <XCircle size={20} />
                    2. Non-Refundable Situations
                  </h2>
                  <div className="text-muted">
                    <p className="mb-3">Refunds will NOT be provided in the following cases:</p>
                    <ul>
                      <li><strong>Service Delivered:</strong> Once your personalized college list has been successfully generated and delivered</li>
                      <li><strong>Change of Mind:</strong> If you simply change your mind after receiving the service</li>
                      <li><strong>Dissatisfaction with Results:</strong> If you're not satisfied with the college recommendations provided</li>
                      <li><strong>Incorrect Information:</strong> If you provided incorrect academic information that affected the recommendations</li>
                      <li><strong>External Factors:</strong> Changes in college admission policies or cutoffs after service delivery</li>
                      <li><strong>Partial Use:</strong> If you've downloaded or accessed any part of your personalized list</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3 d-flex align-items-center gap-2">
                    <Clock size={20} />
                    3. Refund Timeline
                  </h2>
                  <div className="text-muted">
                    <p className="mb-3">Our refund process follows these timelines:</p>
                    <ul>
                      <li><strong>Request Window:</strong> Refund requests must be made within 24 hours of payment</li>
                      <li><strong>Processing Time:</strong> We will review and respond to refund requests within 2-3 business days</li>
                      <li><strong>Refund Processing:</strong> Approved refunds will be processed within 5-7 business days</li>
                      <li><strong>Bank Processing:</strong> It may take an additional 3-5 business days for the refund to appear in your account</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3">4. How to Request a Refund</h2>
                  <div className="text-muted">
                    <p className="mb-3">To request a refund, please follow these steps:</p>
                    <ol>
                      <li>Contact us immediately at <strong>vikalpgrad@gmail.com</strong></li>
                      <li>Include your payment transaction ID and order details</li>
                      <li>Clearly explain the reason for your refund request</li>
                      <li>Provide any supporting documentation if applicable</li>
                    </ol>
                    <p className="mt-3">
                      <strong>Phone Support:</strong> You can also call us at +91 7568377278 during business hours (9 AM - 6 PM IST, Monday to Friday)
                    </p>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3">5. Cancellation Policy</h2>
                  <div className="text-muted">
                    <p className="mb-3">You can cancel your order under the following conditions:</p>
                    <ul>
                      <li><strong>Before Processing:</strong> Orders can be cancelled free of charge if the personalized list generation has not yet begun</li>
                      <li><strong>During Processing:</strong> Once we start generating your list, cancellation may not be possible</li>
                      <li><strong>After Delivery:</strong> Orders cannot be cancelled once the service has been delivered</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3">6. Promo Code and Discount Refunds</h2>
                  <div className="text-muted">
                    <p className="mb-3">Special considerations for promotional offers:</p>
                    <ul>
                      <li>Refunds will be processed for the actual amount paid after discounts</li>
                      <li>Promo codes used in cancelled orders will not be restored</li>
                      <li>One-time use promotional codes cannot be reused after a refund</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3">7. Payment Gateway Charges</h2>
                  <div className="text-muted">
                    <p className="mb-3">Important information about payment processing:</p>
                    <ul>
                      <li>Payment gateway charges (if any) are non-refundable</li>
                      <li>Refunds will be processed through the same payment method used for the original transaction</li>
                      <li>Bank charges for international transactions are not covered by Grad Vikalp</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3">8. Dispute Resolution</h2>
                  <div className="text-muted">
                    <p className="mb-3">If you're not satisfied with our refund decision:</p>
                    <ul>
                      <li>You can escalate the matter by emailing vikalpgrad@gmail.com with "Refund Dispute" in the subject line</li>
                      <li>We will review your case within 5 business days</li>
                      <li>Our decision after the review will be final</li>
                      <li>You may also contact your bank or payment provider for assistance</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3">9. Service Guarantee</h2>
                  <div className="text-muted">
                    <p className="mb-3">We guarantee that:</p>
                    <ul>
                      <li>Your personalized college list will be delivered within 48 hours of payment</li>
                      <li>The list will be based on the academic information you provide</li>
                      <li>You will receive a comprehensive report with college recommendations</li>
                      <li>Our customer support team will assist you with any technical issues</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-warning mb-3">10. Contact Information</h2>
                  <div className="text-muted">
                    <p className="mb-3">For refund requests or questions about this policy:</p>
                    <ul className="list-unstyled">
                      <li><strong>Email:</strong> vikalpgrad@gmail.com</li>
                      <li><strong>Phone:</strong> +91 7568377278</li>
                      <li><strong>Business Hours:</strong> 9:00 AM - 6:00 PM IST (Monday to Friday)</li>
                      <li><strong>Address:</strong> Jaipur, India</li>
                    </ul>
                  </div>
                </div>

                <div className="alert alert-info">
                  <strong>Customer Satisfaction:</strong> While we have specific refund conditions, we're committed to ensuring customer satisfaction. If you have any concerns about our service, please reach out to us, and we'll do our best to resolve the issue.
                </div>

                <div className="alert alert-secondary mt-4">
                  <strong>Policy Updates:</strong> This refund policy may be updated from time to time. Any changes will be posted on this page with an updated "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;